import { Typography } from "@mui/material";
import {
	CoreIconButton,
	CoreErrorIndicator,
	CoreTextField,
	CoreErrorIndicatorRow,
	CoreSelectTextField,
	CoreMenuItem,
	CoreChip,
} from "../../core";
import i18nPackage from "../../i18n/i18n";
import styles from "./SessionDetails.module.css";
import DateInfoIcon from "../images/live/date-info.svg?react";
import ProctorIcon from "../images/live/proctor.svg?react";
import ArrowRightIcon from "../images/live/arrow-right.svg?react";
import DurationIcon from "../images/live/duration.svg?react";
import CapacitySessionIcon from "../images/live/capacity-session.svg?react";
import RemoveIcon from "../images/remove.svg?react";
import AddIcon from "../images/add.svg?react";
import CheckFailIcon from "../images/check-fail.svg?react";
import { SessionEntryProps } from "../edit-quiz-steps/LiveSessionStep";
import {
	areLiveSessionTimesChanged,
	checkProctorGroupCancellationTime,
	checkProctorGroupNotice,
	formatDuration,
	getLmsAvailabilityWarning,
	getMaxSessionDate,
	isLiveSessionCapacityChanged,
	isLiveSessionDemoMode,
	isLiveSessionProctorChanged,
	isLiveSessionProctorGroupChanged,
	MAX_DURATION,
	MAX_DURATION_DEMO,
	type LmsScheduleInfo,
	type PendingLiveSession,
} from "./liveHelper";
import React, { useCallback, useEffect, useMemo } from "react";
import { LiveDateTimePicker, SessionEditorHeader } from "./RegistrationWindow";

type SessionDetailsCardProps = {
	icon: React.ReactNode;
	title: string;
	children: React.ReactNode;
	rightLabel?: React.ReactNode;
	error?: React.ReactNode;
};

function SessionDetailsCard({ children, icon, title, rightLabel, error }: SessionDetailsCardProps) {
	return (
		<div className={styles.sessionDetailsCard}>
			<div className={styles.sessionDetailsCardRow}>
				<div className={styles.sessionDetailsIconWrapper}>
					{icon}
					<Typography>{title}</Typography>
					{error && <CoreErrorIndicator error={error} size={20} />}
				</div>
				{rightLabel}
			</div>
			{children}
		</div>
	);
}

type CapacityCardProps = Pick<SessionDetailsProps, "existingSession" | "session" | "onChange"> & {
	isStarted: boolean;
};

function CapacityCard({ isStarted, existingSession, session, onChange }: CapacityCardProps) {
	const { capacity } = session;
	const capacityError = (!existingSession || isLiveSessionCapacityChanged(existingSession, session)) && capacity <= 0;

	const handleOnCapacityChange = useCallback(
		(newCapacity: number) => {
			const safeCapacity = Math.max(1, newCapacity);
			onChange({ ...session, capacity: safeCapacity });
		},
		[onChange, session],
	);

	const disabledTooltip = isStarted
		? i18nPackage.t("editQuizPage.liveSessionStep.sessionDetails.tooltip.capacity")
		: undefined;

	return (
		<SessionDetailsCard
			title={i18nPackage.t("editQuizPage.liveSessionStep.sessionDetails.sessionCapacity")}
			icon={<CapacitySessionIcon />}
			error={capacityError ? i18nPackage.t("editQuizPage.liveSessionStep.sessionDetails.error.capacity") : undefined}
		>
			<div className={styles.capacityControlRow}>
				<div className={styles.capacityButtonWrapper}>
					<CoreIconButton
						variant="plain"
						className={styles.capacityButton}
						disabled={isStarted}
						disabledTooltip={disabledTooltip}
						onClick={() => handleOnCapacityChange(capacity - 1)}
						aria-label={i18nPackage.t("editQuizPage.liveSessionStep.sessionDetails.ariaLabels.removeCapacity")}
					>
						<RemoveIcon />
					</CoreIconButton>
					<CoreTextField
						value={capacity}
						onChange={(e) => {
							const rawValue = e.target.value;

							if (rawValue === "") {
								handleOnCapacityChange(1);
								return;
							}
							const num = Number(rawValue);
							if (!Number.isInteger(num)) {
								return;
							}

							handleOnCapacityChange(num);
						}}
						disabled={isStarted}
						disabledTooltip={disabledTooltip}
						error={capacityError}
						className={styles.capacityTextField}
						ariaLabel={i18nPackage.t("editQuizPage.liveSessionStep.sessionDetails.ariaLabels.capacity")}
					/>
					<CoreIconButton
						variant="plain"
						className={`${styles.capacityButton} ${styles.add}`}
						disabled={isStarted}
						disabledTooltip={disabledTooltip}
						onClick={() => handleOnCapacityChange(capacity + 1)}
						aria-label={i18nPackage.t("editQuizPage.liveSessionStep.sessionDetails.ariaLabels.addCapacity")}
					>
						<AddIcon />
					</CoreIconButton>
				</div>
				<Typography className={styles.maxTestTakers}>
					{i18nPackage.t("editQuizPage.liveSessionStep.sessionDetails.maxTestTakers")}
				</Typography>
			</div>
		</SessionDetailsCard>
	);
}

type DateTimeDurationCardProps = Pick<
	SessionDetailsProps,
	"existingSession" | "session" | "onChange" | "lmsSchedule"
> & {
	isStarted: boolean;
	currTime: Date;
	isDemoMode: boolean;
};

function DateTimeDurationCard({
	isStarted,
	onChange,
	session,
	existingSession,
	currTime,
	isDemoMode,
	lmsSchedule,
}: DateTimeDurationCardProps) {
	const { startTime, endTime } = session;

	const timesChanged = !existingSession || areLiveSessionTimesChanged(existingSession, session);
	const dateOrderError = timesChanged && startTime >= endTime;
	const duration = endTime.getTime() - startTime.getTime();
	const maxDuration = isDemoMode ? MAX_DURATION_DEMO : MAX_DURATION;
	const durationError = timesChanged && !isDemoMode && duration > maxDuration;
	const availabilityWarning = getLmsAvailabilityWarning(session, lmsSchedule);

	// Check if Start is in the past
	const startDatePastError = timesChanged && startTime < currTime;

	// Check if End is in the past
	const endDatePastError = timesChanged && endTime < currTime;

	const handleStartTimeChange = useCallback(
		(date: Date | null) => {
			if (date) {
				onChange({
					...session,
					startTime: date,
					endTime: isDemoMode ? new Date(date.getTime() + (endTime.getTime() - startTime.getTime())) : endTime,
				});
			}
		},
		[isDemoMode, endTime, onChange, session, startTime],
	);

	const handleEndTimeChange = useCallback(
		(date: Date | null) => {
			if (date) {
				onChange({
					...session,
					endTime: date,
				});
			}
		},
		[onChange, session],
	);

	const maxEndDate = useMemo(() => getMaxSessionDate(session, existingSession).maxEnd, [existingSession, session]);
	const availabilityWarningMessage = availabilityWarning
		? i18nPackage.t("editQuizPage.liveSessionStep.sessionDetails.availabilityWarningInline")
		: undefined;
	const startAvailabilityWarning = availabilityWarning === "startsBeforeAvailability";
	const endAvailabilityWarning = availabilityWarning === "endsAfterAvailability";

	const validationError = startDatePastError
		? isDemoMode
			? i18nPackage.t("editQuizPage.liveSessionStep.sessionDetails.error.demoStart")
			: i18nPackage.t("editQuizPage.liveSessionStep.sessionDetails.error.start")
		: endDatePastError
			? i18nPackage.t("editQuizPage.liveSessionStep.sessionDetails.error.end")
			: dateOrderError
				? i18nPackage.t("editQuizPage.liveSessionStep.sessionDetails.error.timeOrder")
				: durationError
					? i18nPackage.t("editQuizPage.liveSessionStep.sessionDetails.error.duration")
					: availabilityWarning
						? availabilityWarningMessage
						: undefined;

	return (
		<div className={styles.timeDetailsCard}>
			<div className={styles.timePickerRow}>
				<LiveDateTimePicker
					label={i18nPackage.t("editQuizPage.liveSessionStep.sessionDetails.startDateLabel")}
					date={startTime}
					disablePast={true}
					onChange={handleStartTimeChange}
					disabled={isStarted}
					error={startDatePastError || (isDemoMode && dateOrderError) || startAvailabilityWarning}
					dialogAriaLabel={i18nPackage.t(
						"editQuizPage.liveSessionStep.sessionDetails.ariaLabels.startDateLiveSessionDialog",
					)}
					slotProps={{
						textField: {
							disabledTooltip: isStarted
								? isDemoMode
									? i18nPackage.t("editQuizPage.liveSessionStep.sessionDetails.tooltip.demoStart")
									: i18nPackage.t("editQuizPage.liveSessionStep.sessionDetails.tooltip.start")
								: undefined,
						},
					}}
					formControlError={
						startDatePastError
							? isDemoMode
								? i18nPackage.t("editQuizPage.liveSessionStep.sessionDetails.error.demoStart")
								: i18nPackage.t("editQuizPage.liveSessionStep.sessionDetails.error.start")
							: dateOrderError
								? i18nPackage.t("editQuizPage.liveSessionStep.sessionDetails.error.dateOrderStart")
								: startAvailabilityWarning
									? availabilityWarningMessage
									: undefined
					}
				/>
				<ArrowRightIcon className={styles.timeArrow} aria-hidden />
				<LiveDateTimePicker
					label={i18nPackage.t("editQuizPage.liveSessionStep.sessionDetails.endDateLabel")}
					date={endTime}
					disablePast
					disabled={isStarted}
					error={endDatePastError || dateOrderError || endAvailabilityWarning}
					dialogAriaLabel={i18nPackage.t(
						"editQuizPage.liveSessionStep.sessionDetails.ariaLabels.endDateLiveSessionDialog",
					)}
					slotProps={{
						textField: {
							disabledTooltip: isStarted
								? i18nPackage.t("editQuizPage.liveSessionStep.sessionDetails.tooltip.end")
								: undefined,
						},
					}}
					formControlError={
						endDatePastError
							? i18nPackage.t("editQuizPage.liveSessionStep.sessionDetails.error.end")
							: dateOrderError
								? i18nPackage.t("editQuizPage.liveSessionStep.sessionDetails.error.dateOrderEnd")
								: endAvailabilityWarning
									? availabilityWarningMessage
									: undefined
					}
					onChange={handleEndTimeChange}
					maxDateTime={maxEndDate}
				/>
			</div>
			<div className={styles.timeFooterRow}>
				{validationError ? (
					<div className={styles.validationError}>
						<CheckFailIcon aria-hidden />
						<Typography>{validationError}</Typography>
					</div>
				) : (
					<div />
				)}
				<div className={styles.totalDuration}>
					<DurationIcon aria-hidden />
					<Typography>{i18nPackage.t("editQuizPage.liveSessionStep.sessionDetails.totalDuration")}</Typography>
					<span className={styles.durationBadge}>{formatDuration(duration)}</span>
				</div>
			</div>
		</div>
	);
}

type ProctorCardProps = Pick<
	SessionDetailsProps,
	"existingSession" | "session" | "onChange" | "availableProctorGroups" | "availableProctors"
>;

function ProctorCard({
	availableProctorGroups,
	availableProctors,
	onChange,
	session,
	existingSession,
}: ProctorCardProps) {
	const { proctorId, proctorGroupId, startTime } = session;

	// Default to Proctor Group if there is one
	const isProctorGroup = useMemo(() => {
		const numProctorGroups = Object.keys(availableProctorGroups).length;
		return proctorGroupId !== undefined || numProctorGroups > 0;
	}, [availableProctorGroups, proctorGroupId]);

	const numProctors = useMemo(() => {
		const finalAvailableProctors = isProctorGroup ? availableProctorGroups : availableProctors;
		return Object.keys(finalAvailableProctors).length;
	}, [availableProctorGroups, availableProctors, isProctorGroup]);

	const proctorChanged =
		!existingSession ||
		isLiveSessionProctorChanged(existingSession, session) ||
		isLiveSessionProctorGroupChanged(existingSession, session);
	const noProctorError = proctorChanged && !proctorId && !proctorGroupId;

	const selectedProctorGroup = proctorGroupId ? availableProctorGroups[proctorGroupId] : undefined;
	const proctorGroupNoticeError =
		proctorChanged && selectedProctorGroup && !checkProctorGroupNotice(session.startTime, selectedProctorGroup);

	const setSessionProctorGroup = useCallback(
		(proctorGroupId: number) => {
			const updatedSession = { ...session, proctorId: undefined, proctorGroupId };
			const proctorGroup = availableProctorGroups[proctorGroupId];
			if (
				proctorGroup?.autoCancelEmptyHoursBefore != null &&
				!checkProctorGroupCancellationTime(updatedSession, proctorGroup)
			) {
				updatedSession.registrationEndTime = new Date(
					updatedSession.startTime.getTime() - proctorGroup.autoCancelEmptyHoursBefore * 60 * 60 * 1000,
				);
				if (!updatedSession.registrationStartTime) {
					const currTimestamp = Date.now();
					updatedSession.registrationStartTime = new Date(
						Math.min(currTimestamp + 60 * 60 * 1000, updatedSession.startTime.getTime() - 60 * 1000),
					);
				}
			}
			return updatedSession;
		},
		[availableProctorGroups, session],
	);

	const handleProctorChange = useCallback(
		(id: string | undefined) => {
			onChange({
				...session,
				proctorId: isProctorGroup ? undefined : id,
				proctorGroupId: isProctorGroup ? Number(id) : undefined,
			});
		},
		[isProctorGroup, onChange, session],
	);

	const proctorErrorText = useMemo(() => {
		const proctorGroupError = noProctorError
			? i18nPackage.t("editQuizPage.liveSessionStep.proctor.noProctor")
			: proctorGroupNoticeError && selectedProctorGroup
				? i18nPackage.t("editQuizPage.liveSessionStep.proctor.insufficientNotice", {
						count: selectedProctorGroup.minHoursNotice,
					})
				: undefined;

		const proctorError = noProctorError ? i18nPackage.t("editQuizPage.liveSessionStep.proctor.noProctor") : undefined;

		return isProctorGroup ? proctorGroupError : proctorError;
	}, [isProctorGroup, noProctorError, proctorGroupNoticeError, selectedProctorGroup]);

	useEffect(() => {
		if (isProctorGroup && !proctorGroupId) {
			const firstValidGroup = Object.values(availableProctorGroups).find((group) =>
				checkProctorGroupNotice(startTime, group),
			);
			if (firstValidGroup) {
				const updatedSession = setSessionProctorGroup(firstValidGroup.id);
				onChange(updatedSession);
			}
		}
	}, [availableProctorGroups, isProctorGroup, onChange, proctorGroupId, setSessionProctorGroup, startTime]);

	return (
		<SessionDetailsCard
			icon={<ProctorIcon />}
			title={i18nPackage.t("editQuizPage.liveSessionStep.sessionDetails.assignProctor")}
		>
			<div className={styles.proctorContainer}>
				{numProctors === 1 ? (
					<Typography className={styles.fixedProctor}>
						{isProctorGroup
							? Object.values(availableProctorGroups)[0].name
							: (Object.values(availableProctors)[0].name ??
								i18nPackage.t("editQuizPage.liveSessionStep.sessionDetails.defaultProctor"))}
					</Typography>
				) : (
					<CoreErrorIndicatorRow error={proctorErrorText} className={styles.proctorErrorRow}>
						<CoreSelectTextField<string>
							value={(isProctorGroup ? proctorGroupId : proctorId) ?? null}
							onChange={(e) => handleProctorChange(e.target.value)}
							label={i18nPackage.t("editQuizPage.liveSessionStep.proctor.selectProctor")}
							renderValue={(id) =>
								isProctorGroup ? availableProctorGroups[Number(id)]?.name : availableProctors[id]?.name
							}
							error={noProctorError}
							popperMaxHeight="250px"
						>
							{!isProctorGroup &&
								Object.values(availableProctors).map(({ id, name }) => (
									<CoreMenuItem key={id} value={id}>
										{name || i18nPackage.t("editQuizPage.liveSessionStep.sessionDetails.defaultProctor")}
									</CoreMenuItem>
								))}

							{isProctorGroup &&
								Object.values(availableProctorGroups).map((proctorGroup) => {
									const { id, name, minHoursNotice } = proctorGroup;
									const insufficientNotice =
										!checkProctorGroupNotice(startTime, proctorGroup) &&
										!existingSession?.invitations.some(({ proctorGroupId }) => proctorGroupId === id);
									return (
										<CoreMenuItem
											className={styles.dropdownItem}
											key={id}
											value={String(id)}
											disabled={insufficientNotice}
											disabledTooltip={i18nPackage.t("editQuizPage.liveSessionStep.proctor.insufficientNotice", {
												count: minHoursNotice,
											})}
											slotProps={{
												tooltip: {
													placement: "right",
													slotProps: { tooltip: { sx: { maxWidth: 200 } } },
												},
											}}
										>
											{name}
											<CoreChip chipColor="#DBE7E1" className={styles.minNoticeBadge}>
												{insufficientNotice
													? i18nPackage.t("editQuizPage.liveSessionStep.proctor.unavailable")
													: i18nPackage.t("editQuizPage.liveSessionStep.proctor.available")}
											</CoreChip>
										</CoreMenuItem>
									);
								})}
						</CoreSelectTextField>
					</CoreErrorIndicatorRow>
				)}
			</div>
		</SessionDetailsCard>
	);
}

type SessionDetailsProps = Pick<
	SessionEntryProps,
	"onChange" | "existingSession" | "availableProctorGroups" | "availableProctors"
> & {
	session: PendingLiveSession;
	lmsSchedule?: LmsScheduleInfo;
};

export default function SessionDetails({
	onChange,
	session,
	existingSession,
	availableProctorGroups,
	availableProctors,
	lmsSchedule,
}: SessionDetailsProps) {
	const currTime = new Date();

	const demoMode = isLiveSessionDemoMode(session);

	const isStarted = !!existingSession && currTime >= new Date(existingSession.startTime);

	return (
		<div className={styles.sessionDetailsContainer}>
			<SessionEditorHeader
				icon={<DateInfoIcon width={20} height={20} />}
				title={i18nPackage.t("editQuizPage.liveSessionStep.sessionDetails.title")}
				description={i18nPackage.t("editQuizPage.liveSessionStep.sessionDetails.description")}
			/>
			<div className={styles.sessionDetailsGrid}>
				<DateTimeDurationCard
					currTime={currTime}
					isStarted={isStarted}
					onChange={onChange}
					session={session}
					existingSession={existingSession}
					isDemoMode={demoMode}
					lmsSchedule={lmsSchedule}
				/>
				<CapacityCard isStarted={isStarted} onChange={onChange} session={session} existingSession={existingSession} />
				<ProctorCard
					availableProctorGroups={availableProctorGroups}
					availableProctors={availableProctors}
					onChange={onChange}
					session={session}
					existingSession={existingSession}
				/>
			</div>
		</div>
	);
}
